/**
 * LeaseEnvelopes — list e-sign envelopes for a lease. Landlords can
 * create a new envelope (paste the lease body + tenant email). Both
 * parties can click an envelope to sign or download the signed PDF.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Card, Button, Pill, Loader, Input, Textarea, Modal, EmptyState } from "@/components/ui/Primitives";
import { FileSignature, Plus, Download, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

export default function LeaseEnvelopes() {
  const { leaseId } = useParams();
  const { user } = useAuth();
  const isLandlord = (user?.role || "").toLowerCase() === "landlord";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/esign/lease/${leaseId}/envelopes`, { headers: auth() });
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, [leaseId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader/>;
  return (
    <div data-testid="lease-envelopes-page">
      <Link to="/leases" className="text-xs text-slate-600 hover:text-slate-900 font-semibold">← Back to leases</Link>
      <PageHeader
        title="E-signature envelopes"
        subtitle={`Lease ${leaseId} — signed PDFs are available here anytime.`}
      />
      {isLandlord && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setOpenNew(true)} data-testid="new-envelope-btn"><Plus className="w-4 h-4 inline mr-1"/>Send for signature</Button>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={FileSignature} title="No envelopes yet" description={isLandlord ? "Send this lease to your tenant for e-signature." : "Your landlord hasn't sent you anything to sign yet."}/>
      ) : (
        <div className="grid gap-3">
          {items.map((e) => (
            <Card key={e.id} className="p-4" data-testid={`envelope-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Envelope {e.id} · sent {new Date(e.created_at).toLocaleDateString()}</div>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {e.signers?.map((s) => (
                      <Pill key={s.role} tone={s.signed_at ? "green" : "amber"}>
                        {s.signed_at ? <CheckCircle2 className="w-3 h-3 inline mr-0.5"/> : <Clock className="w-3 h-3 inline mr-0.5"/>}
                        {s.role}
                      </Pill>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Link to={`/esign/${e.id}`} data-testid={`envelope-open-${e.id}`}>
                    <Button variant="secondary" className="!py-1.5 !px-3 text-xs">Open <ArrowRight className="w-3 h-3 inline ml-1"/></Button>
                  </Link>
                  {e.status === "completed" && (
                    <a href={`${API}/api/esign/envelopes/${e.id}/pdf`} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-emerald-700 hover:underline inline-flex items-center gap-0.5" data-testid={`envelope-pdf-${e.id}`}>
                      <Download className="w-3 h-3"/> PDF
                    </a>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {openNew && <NewEnvelopeModal leaseId={leaseId} onClose={() => setOpenNew(false)} onCreated={(env) => { setOpenNew(false); setItems((s) => [env, ...s]); }}/>}
    </div>
  );
}

function NewEnvelopeModal({ leaseId, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", tenant_email: "", body_text: "" });
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!form.tenant_email.trim() || !form.body_text.trim()) return toast.error("Tenant email and lease body are required.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/esign/envelopes`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lease_id: leaseId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not create");
      toast.success("Envelope created — the tenant can now sign.");
      onCreated(d);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }
  return (
    <Modal open={true} onClose={onClose} title="Send lease for signature" testid="new-envelope-modal">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Envelope title" placeholder="12-month residential lease" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testid="env-title-input"/>
        <Input label="Tenant email" type="email" required value={form.tenant_email} onChange={(e) => setForm({ ...form, tenant_email: e.target.value })} testid="env-tenant-email"/>
        <Textarea
          label="Lease body text"
          required
          placeholder="Paste the full lease agreement text here. Include all clauses, rent, terms, and rules."
          value={form.body_text}
          onChange={(e) => setForm({ ...form, body_text: e.target.value })}
          testid="env-body-text"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="env-submit">{busy ? "Sending…" : "Create envelope"}</Button>
        </div>
      </form>
    </Modal>
  );
}
